import React, { useState } from 'react'
import '../sso-workspace/sso-workspace.scss'
import './sso-consent.scss'
import { normalizeDomain } from '../form-new/form-tags'
import { ConsentRequestStatus } from './consent-request-status'
import { emailProblem, serverMessage, tenantInputProblem } from './consent-model'
import { ErrorReference } from '../error/error-reference'

const COPY = {
  staff: {
    microsoft: "The customer's IT administrator approves EducationXR in their Microsoft organization. We email them a link that explains the request first.",
    email: "We email them a page that explains the request, then sends them to Microsoft. Leave it empty to copy the link and send it yourself.",
    google: "Google Workspace has no approval step. The connection is created for the organization with its email domain, and goes live once the domain is verified: the organization's admins can verify it, or you can override it."
  },
  org: {
    microsoft: 'Your IT administrator approves EducationXR in your Microsoft organization. We email them a link that explains the request first.',
    email: "We email them a page that explains the request, then sends them to Microsoft. Leave it empty to copy the link and send it yourself.",
    google: 'Google Workspace has no approval step. The connection is created now and goes live once your email domain is verified.'
  }
}

// One connect flow for both the staff connect page and an organization's own
// Authentication page: choose Microsoft or Google, then only that provider's
// fields. Microsoft records a consent request (emailed, or a link to copy)
// and shows its status panel; Google creates the connection directly.
// `lead` renders above both (the staff page's organization picker) and
// `ready` holds both back until it is filled in.
//
// Validation is on blur and on submit, as a sentence under the field -- never
// a red "required" under every empty input.
export const ConnectProvider = ({
  audience = 'org',
  enabled,
  ready = true,
  notReadyHint,
  initialTenant = '',
  initialProvider = 'microsoft',
  lead,
  onRequest,
  onResend,
  onCancel,
  onRequested,
  onGoogle
}) => {
  const copy = COPY[audience] || COPY.org
  const [provider, setProvider] = useState(initialProvider)

  return (
    <div className="sc-connect">
      <fieldset className="sc-choices" disabled={!enabled}>
        <legend className="sw-label mb-2">Identity provider</legend>
        <label className={`sw-choice sc-choice${provider === 'microsoft' ? ' sw-checked' : ''}`}>
          <input type="radio" name={`sc-provider-${audience}`} className="form-check-input mt-1" value="microsoft" checked={provider === 'microsoft'} onChange={() => setProvider('microsoft')} />
          <span>
            <span className="d-block fw-semibold">Microsoft Entra ID</span>
            <span className="sw-hint">Office 365 and Microsoft work accounts. Needs your IT administrator's approval.</span>
          </span>
        </label>
        <label className={`sw-choice sc-choice${provider === 'google' ? ' sw-checked' : ''}`}>
          <input type="radio" name={`sc-provider-${audience}`} className="form-check-input mt-1" value="google" checked={provider === 'google'} onChange={() => setProvider('google')} />
          <span>
            <span className="d-block fw-semibold">Google Workspace</span>
            <span className="sw-hint">Google work accounts. No approval step; live once the domain is verified.</span>
          </span>
        </label>
      </fieldset>

      {lead && <div className="mt-3">{lead}</div>}
      {enabled && !ready && notReadyHint && <p className="sw-hint mt-2 mb-0">{notReadyHint}</p>}

      {provider === 'microsoft'
        ? <MicrosoftRequest copy={copy} enabled={enabled} ready={ready} initialTenant={initialTenant}
          onRequest={onRequest} onResend={onResend} onCancel={onCancel} onRequested={onRequested} />
        : <GoogleConnect copy={copy} enabled={enabled && ready} onGoogle={onGoogle} />}
    </div>
  )
}

// `ready` is false until the page's own `lead` field (the staff page's
// organization picker, shown above both providers) is filled in.
const MicrosoftRequest = ({ copy, ready, enabled: allowed, initialTenant, onRequest, onResend, onCancel, onRequested }) => {
  const enabled = allowed && ready
  const [tenant, setTenant] = useState(initialTenant || '')
  const [email, setEmail] = useState('')
  const [touched, setTouched] = useState({})
  const [error, setError] = useState(null)
  const [cause, setCause] = useState(null)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)

  const tenantHint = touched.tenant ? tenantInputProblem(tenant) : null
  const emailHint = touched.email ? emailProblem(email) : null

  const submit = async send => {
    setTouched({ tenant: true, email: send })
    setError(null)
    setCause(null)

    if (tenantInputProblem(tenant) || (send && (emailProblem(email) || !email.trim()))) return

    setBusy(true)
    try {
      const response = await onRequest({ tenant: tenant.trim(), sendTo: send ? email.trim() : '' })
      setResult(response)
      setTouched({})
      onRequested && (await onRequested(response))
    } catch (err) {
      setError(serverMessage(err, "The request couldn't be created. Try again."))
      setCause(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="sc-form mt-3" noValidate onSubmit={e => { e.preventDefault(); submit(true) }}>
      <p className="sw-hint">{copy.microsoft}</p>

      <div className="sc-fields">
        <div className="sw-field">
          <label className="sw-label" htmlFor="sc-tenant">Organization domain or tenant ID</label>
          <input id="sc-tenant" className="form-control" value={tenant} placeholder="example.org" autoComplete="off" spellCheck={false}
            disabled={!enabled} aria-invalid={!!tenantHint} aria-describedby="sc-tenant-hint"
            onChange={e => setTenant(e.target.value)} onBlur={() => setTouched(t => ({ ...t, tenant: true }))} />
          <div id="sc-tenant-hint" className={tenantHint ? 'sw-required' : 'sw-hint'}>
            {tenantHint || 'The domain people sign in with, or the Directory (tenant) ID from the Microsoft Entra admin center.'}
          </div>
        </div>

        <div className="sw-field">
          <label className="sw-label" htmlFor="sc-email">IT administrator's email <span className="sw-hint fw-normal ms-1">Optional</span></label>
          <input id="sc-email" type="email" className="form-control" value={email} placeholder="it-admin@example.org" autoComplete="off"
            disabled={!enabled} aria-invalid={!!emailHint} aria-describedby="sc-email-hint"
            onChange={e => setEmail(e.target.value)} onBlur={() => setTouched(t => ({ ...t, email: !!email.trim() }))} />
          <div id="sc-email-hint" className={emailHint ? 'sw-required' : 'sw-hint'}>{emailHint || copy.email}</div>
        </div>
      </div>

      {error && <p className="text-danger small mt-2 mb-0" role="alert">{error}</p>}
      {error && <ErrorReference error={cause} />}

      <div className="d-flex flex-wrap gap-2 mt-3">
        <button type="submit" className="btn sw-btn sw-btn-primary" disabled={!enabled || busy || !email.trim()}>Send request</button>
        <button type="button" className="btn sw-btn" disabled={!enabled || busy} onClick={() => submit(false)}>Generate link</button>
      </div>

      {result &&
        <div className="mt-3">
          <ConsentRequestStatus key={result.request.id} result={result} enabled={allowed} onResend={onResend} onCancel={onCancel} />
        </div>}
    </form>
  )
}

const GoogleConnect = ({ copy, enabled, onGoogle }) => {
  const [domain, setDomain] = useState('')
  const [name, setName] = useState('')
  const [touched, setTouched] = useState({})
  const [error, setError] = useState(null)
  const [cause, setCause] = useState(null)
  const [busy, setBusy] = useState(false)

  const domainHint = touched.domain && !normalizeDomain(domain) ? (domain.trim() ? `"${domain.trim()}" isn't a domain.` : 'Enter the email domain, for example example.org.') : null
  const nameHint = touched.name && !name.trim() ? 'Give the connection a name people will recognise.' : null

  const handleSubmit = async e => {
    e.preventDefault()
    setTouched({ domain: true, name: true })
    setError(null)
    setCause(null)

    const normalized = normalizeDomain(domain)
    if (!normalized || !name.trim()) return

    setBusy(true)
    try {
      await onGoogle({ domain: normalized, name: name.trim() })
      setDomain('')
      setName('')
      setTouched({})
    } catch (err) {
      setError(serverMessage(err, "The connection couldn't be created. Try again."))
      setCause(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="sc-form mt-3" noValidate onSubmit={handleSubmit}>
      <p className="sw-hint">{copy.google}</p>

      <div className="sc-fields">
        <div className="sw-field">
          <label className="sw-label" htmlFor="sc-google-domain">Email domain</label>
          <input id="sc-google-domain" className="form-control" value={domain} placeholder="example.org" autoComplete="off" spellCheck={false}
            disabled={!enabled} aria-invalid={!!domainHint} aria-describedby="sc-google-domain-hint"
            onChange={e => setDomain(e.target.value)} onBlur={() => setTouched(t => ({ ...t, domain: true }))} />
          <div id="sc-google-domain-hint" className={domainHint ? 'sw-required' : 'sw-hint'}>{domainHint || 'People whose address ends in this domain sign in with Google.'}</div>
        </div>

        <div className="sw-field">
          <label className="sw-label" htmlFor="sc-google-name">Connection name</label>
          <input id="sc-google-name" className="form-control" value={name} placeholder="Example Org Google" autoComplete="off"
            disabled={!enabled} aria-invalid={!!nameHint} aria-describedby="sc-google-name-hint"
            onChange={e => setName(e.target.value)} onBlur={() => setTouched(t => ({ ...t, name: true }))} />
          <div id="sc-google-name-hint" className={nameHint ? 'sw-required' : 'sw-hint'}>{nameHint || 'Shown on the sign-in button and in the connection list.'}</div>
        </div>
      </div>

      {error && <p className="text-danger small mt-2 mb-0" role="alert">{error}</p>}
      {error && <ErrorReference error={cause} />}

      <div className="d-flex flex-wrap gap-2 mt-3">
        <button type="submit" className="btn sw-btn sw-btn-primary" disabled={!enabled || busy}>Connect Google Workspace</button>
      </div>
    </form>
  )
}
